import './settings-leaf.css';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../ui.tsx';
import { CardSaveFooter, useCompanyCardForm } from './company-card-form.tsx';
import type { Company } from '@shared/types/index.ts';

/**
 * Who hears from the app and through which channel: the team when customers
 * act on quotes or ask to move a visit, and customers when an invoice is
 * ready. Split out of the follow-ups card, which had quietly become three
 * topics under one heading.
 */

const snapshot = (company: Company) => ({
  notifyOnQuoteEvents: company.notifyOnQuoteEvents,
  notifyOwnersOnChangeRequest: company.notifyOwnersOnChangeRequest,
  notifyAssigneeOnChangeRequest: company.notifyAssigneeOnChangeRequest,
  textChangeRequestAlerts: company.textChangeRequestAlerts,
  notifyOwnersOnVisitCancelled: company.notifyOwnersOnVisitCancelled,
  emailInvoicesToCustomers: company.emailInvoicesToCustomers,
  textInvoicesToCustomers: company.textInvoicesToCustomers,
  emailReceiptsToCustomers: company.emailReceiptsToCustomers,
});

const toPayload = (draft: ReturnType<typeof snapshot>) => ({
  notifyOnQuoteEvents: draft.notifyOnQuoteEvents,
  notifyOwnersOnChangeRequest: draft.notifyOwnersOnChangeRequest,
  notifyAssigneeOnChangeRequest: draft.notifyAssigneeOnChangeRequest,
  textChangeRequestAlerts: draft.textChangeRequestAlerts,
  notifyOwnersOnVisitCancelled: draft.notifyOwnersOnVisitCancelled,
  emailInvoicesToCustomers: draft.emailInvoicesToCustomers,
  textInvoicesToCustomers: draft.textInvoicesToCustomers,
  emailReceiptsToCustomers: draft.emailReceiptsToCustomers,
});

export default function NotificationsCard() {
  const { t } = useTranslation();
  const form = useCompanyCardForm(snapshot, toPayload);

  const draft = form.draft;
  if (!draft) return null;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="notifications-heading">
      <div className="work-section-heading">
        <h2 id="notifications-heading" className="mb-2 text-lg font-semibold">
          {t('settings.notificationsHeading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.notificationsDescription')}</p>

      <Checkbox
        label={t('settings.notifyOnQuoteEvents')}
        hint={t('settings.notifyOnQuoteEventsHint')}
        checked={draft.notifyOnQuoteEvents}
        disabled={form.readOnly}
        onChange={(event) => form.set('notifyOnQuoteEvents', event.target.checked)}
      />

      <h3 className="mt-6 mb-1 text-sm font-semibold">{t('settings.changeRequestAlertsHeading')}</h3>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('settings.changeRequestAlertsHint')}</p>

      <Checkbox
        label={t('settings.notifyOwnersOnChangeRequest')}
        hint={t('settings.notifyOwnersOnChangeRequestHint')}
        checked={draft.notifyOwnersOnChangeRequest}
        disabled={form.readOnly}
        onChange={(event) => form.set('notifyOwnersOnChangeRequest', event.target.checked)}
      />
      <Checkbox
        label={t('settings.notifyAssigneeOnChangeRequest')}
        hint={t('settings.notifyAssigneeOnChangeRequestHint')}
        checked={draft.notifyAssigneeOnChangeRequest}
        disabled={form.readOnly}
        onChange={(event) => form.set('notifyAssigneeOnChangeRequest', event.target.checked)}
      />
      <Checkbox
        label={t('settings.textChangeRequestAlerts')}
        hint={t('settings.textChangeRequestAlertsHint')}
        checked={draft.textChangeRequestAlerts}
        disabled={form.readOnly}
        onChange={(event) => form.set('textChangeRequestAlerts', event.target.checked)}
      />

      <h3 className="mt-6 mb-1 text-sm font-semibold">{t('settings.visitCancelledHeading')}</h3>
      <Checkbox
        label={t('settings.notifyOwnersOnVisitCancelled')}
        hint={t('settings.notifyOwnersOnVisitCancelledHint')}
        checked={draft.notifyOwnersOnVisitCancelled}
        disabled={form.readOnly}
        onChange={(event) => form.set('notifyOwnersOnVisitCancelled', event.target.checked)}
      />

      {/* On every plan, Free included: a payment recorded on a quote emails
          the customer a receipt unless this is off. */}
      <h3 className="mt-6 mb-1 text-sm font-semibold">{t('settings.receiptsHeading')}</h3>
      <Checkbox
        label={t('settings.emailReceiptsToCustomers')}
        hint={t('settings.emailReceiptsToCustomersHint')}
        checked={draft.emailReceiptsToCustomers}
        disabled={form.readOnly}
        onChange={(event) => form.set('emailReceiptsToCustomers', event.target.checked)}
      />

      <h3 className="mt-6 mb-1 text-sm font-semibold">{t('settings.invoiceNoticesHeading')}</h3>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('settings.invoiceNoticesHint')}</p>

      <Checkbox
        label={t('settings.emailInvoicesToCustomers')}
        hint={t('settings.emailInvoicesToCustomersHint')}
        checked={draft.emailInvoicesToCustomers}
        disabled={form.readOnly}
        onChange={(event) => form.set('emailInvoicesToCustomers', event.target.checked)}
      />
      <Checkbox
        label={t('settings.textInvoicesToCustomers')}
        hint={t('settings.textInvoicesToCustomersHint')}
        checked={draft.textInvoicesToCustomers}
        disabled={form.readOnly}
        onChange={(event) => form.set('textInvoicesToCustomers', event.target.checked)}
      />

      <CardSaveFooter form={form} saveLabel={t('settings.saveNotifications')} />
    </section>
  );
}
